import { onScopeDispose, ref } from "vue";

import { masterApi } from "@/entities/master";
import type { AppError } from "@/shared/api";
import { i18n } from "@/shared/i18n";
import { toast } from "@/shared/lib";

import type { PhotoErrorKey } from "./mapPhotoError";
import { mapPhotoError } from "./mapPhotoError";

/** Стан upload-флоу (spec §5.2). `idle` (немає/обрано), `uploading`, `error`. */
export type PhotoUploadStatus = "idle" | "uploading" | "error";

/** Стан remove-флоу confirm-діалогу (spec §5.2/S15). */
export type PhotoRemoveState = "idle" | "confirming" | "removing";

/** Колбеки результату — host застосовує до свого стану (spec §4.2/§5.2). */
export interface UseMasterPhotoOptions {
  /** 200 upload → новий `photo_url` (server-authoritative, живе в host-`master`). */
  onChanged: (photoUrl: string | null) => void;
  /** 404/`invalid_id` → майстра вже немає (host показує notFound-панель). */
  onNotFound: () => void;
}

function tr(key: PhotoErrorKey): string {
  return i18n.global.t(key);
}

/**
 * Екранний стан photo-флоу (spec §5.2, S5). **Composable, не store** — кожен
 * маунт вкладки = чистий інстанс. Тримає transient upload-стан + AbortController;
 * `photo_url` тут **не** зберігається (server-authoritative, живе в host-`master`).
 * Виконує операцію й віддає результат через `onChanged`/`onNotFound`; навігації/
 * announce не робить (це host). Cleanup: revoke object-URL + abort на unmount.
 *
 * Upload/replace: `selectFile`/`clearSelection`/`confirmUpload`/`cancel`.
 * Видалення (confirm-діалог S15): `requestRemove`/`confirmRemove`/`cancelRemove`
 * (`removeState`/`removeErrorKey`) — `photo_url` зникає лише на 204 (S6).
 */
export function useMasterPhoto(masterId: string, opts: UseMasterPhotoOptions) {
  const status = ref<PhotoUploadStatus>("idle");
  const progress = ref<number | "indeterminate">("indeterminate");
  const errorKey = ref<PhotoErrorKey | null>(null);
  const selectedFile = ref<File | null>(null);
  /** object-URL локального прев'ю обраного файлу (до upload). Анти-leak — revoke. */
  const previewUrl = ref<string | null>(null);
  /** Стан confirm-діалогу видалення (S15). `photo_url` міняється лише на 204 (S6). */
  const removeState = ref<PhotoRemoveState>("idle");
  const removeErrorKey = ref<PhotoErrorKey | null>(null);

  let controller: AbortController | null = null;
  /** Прапор «це скасування» — у catch розрізняємо abort від справжньої помилки,
   *  не імпортуючи axios у feature-шар (spec §5.2). */
  let canceled = false;

  function revokePreview(): void {
    if (previewUrl.value) {
      URL.revokeObjectURL(previewUrl.value);
      previewUrl.value = null;
    }
  }

  /** Обрати файл → локальне прев'ю (object-URL); upload не стартує (explicit-confirm S11). */
  function selectFile(file: File): void {
    revokePreview();
    selectedFile.value = file;
    previewUrl.value = URL.createObjectURL(file);
    status.value = "idle";
    errorKey.value = null;
    progress.value = "indeterminate";
  }

  /** Скинути вибір (revoke прев'ю) — фото лишається попереднім (S6). */
  function clearSelection(): void {
    revokePreview();
    selectedFile.value = null;
    status.value = "idle";
    errorKey.value = null;
    progress.value = "indeterminate";
  }

  /**
   * Підтвердити upload (S11). Single-flight: під час `uploading` повторний виклик —
   * no-op. 200 → revoke прев'ю + reset + `onChanged(url)`. Помилка: `inline` →
   * `status='error'`+`errorKey` (фото недоторкане, S6); `notFound` → `onNotFound`;
   * `toast` → foreground toast. Cancel → тихий reset (без error/toast).
   */
  async function confirmUpload(): Promise<void> {
    if (!selectedFile.value || status.value === "uploading") {
      return;
    }
    const file = selectedFile.value;
    canceled = false;
    controller = new AbortController();
    status.value = "uploading";
    errorKey.value = null;
    progress.value = "indeterminate";
    try {
      const url = await masterApi.uploadMasterPhoto(masterId, file, {
        onProgress: (p) => {
          progress.value = p;
        },
        signal: controller.signal,
      });
      revokePreview();
      selectedFile.value = null;
      status.value = "idle";
      progress.value = "indeterminate";
      opts.onChanged(url);
    } catch (e) {
      if (canceled) {
        // тихий reset — abort не помилка показу (S6/§12.5).
        clearSelection();
        return;
      }
      const mapped = mapPhotoError(e as AppError);
      if (mapped.surface === "notFound") {
        opts.onNotFound();
        return;
      }
      if (mapped.surface === "toast") {
        toast.error(tr(mapped.i18nKey));
        // фото/вибір недоторкані; повернутись у idle (можна повторити).
        status.value = "idle";
        return;
      }
      // inline: попереднє фото видиме й недоторкане (S6), вибір лишається.
      errorKey.value = mapped.i18nKey;
      status.value = "error";
    } finally {
      controller = null;
    }
  }

  /** Скасувати активний upload (FileInput-X) → abort → тихий reset (S6/§7.4). */
  function cancel(): void {
    if (status.value !== "uploading") {
      return;
    }
    canceled = true;
    controller?.abort();
  }

  /** Відкрити confirm-діалог видалення (S15). Запит ще не йде. */
  function requestRemove(): void {
    removeState.value = "confirming";
    removeErrorKey.value = null;
  }

  /** Закрити діалог без видалення — фото недоторкане, запит не йде (S6/§12.5). */
  function cancelRemove(): void {
    removeState.value = "idle";
    removeErrorKey.value = null;
  }

  /**
   * Підтвердити видалення (S15). Single-flight: під час `removing` повторний
   * виклик — no-op. 204 → `onChanged(null)` + reset (S6). 404/`invalid_id` →
   * `onNotFound` (bubble). Інша помилка → `removeErrorKey` + діалог лишається
   * відкритим (`confirming`), фото недоторкане, повтор можливий (ADR-0015 D2).
   */
  async function confirmRemove(): Promise<void> {
    if (removeState.value === "removing") {
      return;
    }
    removeState.value = "removing";
    removeErrorKey.value = null;
    try {
      await masterApi.deleteMasterPhoto(masterId);
      removeState.value = "idle";
      opts.onChanged(null);
      toast.success(i18n.global.t("masters.photo.a11y.removed"));
    } catch (e) {
      const mapped = mapPhotoError(e as AppError);
      if (mapped.surface === "notFound") {
        removeState.value = "idle";
        opts.onNotFound();
        return;
      }
      if (mapped.surface === "toast") {
        toast.error(tr(mapped.i18nKey));
        removeState.value = "idle";
        return;
      }
      // inline у тілі діалогу — фото недоторкане (S6), повтор можливий.
      removeErrorKey.value = mapped.i18nKey;
      removeState.value = "confirming";
    }
  }

  onScopeDispose(() => {
    canceled = true;
    controller?.abort();
    revokePreview();
  });

  return {
    status,
    progress,
    errorKey,
    selectedFile,
    previewUrl,
    removeState,
    removeErrorKey,
    selectFile,
    clearSelection,
    confirmUpload,
    cancel,
    requestRemove,
    cancelRemove,
    confirmRemove,
  };
}

export type MasterPhoto = ReturnType<typeof useMasterPhoto>;
